import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress, Container, MenuItem, TextField } from "@mui/material";
import { ArrowRightIcon, CalendarDaysIcon, MagnifyingGlassIcon, NewspaperIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { format, isWithinInterval, parseISO, subDays } from "date-fns";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/navBar";
import { newsService } from "../services/newsService";

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      return Number.isNaN(date.getTime()) ? "Date not available" : format(date, "MMMM dd, yyyy");
    } catch (dateError) {
      return "Date not available";
    }
  };

  const getFirstImage = (content = "") => {
    const imageMatch = content.match(/<img[^>]+src="([^">]+)"/);
    return imageMatch ? imageMatch[1] : null;
  };

  const getTextContent = (content = "") => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const text = tempDiv.textContent || tempDiv.innerText || "";
    return text.length > 200 ? `${text.substring(0, 200)}...` : text;
  };

  useEffect(() => {
    let mounted = true;
    newsService.getAllNews()
      .then((data) => {
        if (mounted) setNews(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (mounted) setError("Failed to fetch news. Please try again later.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const filteredNews = useMemo(() => {
    let result = [...news];
    const query = searchQuery.trim().toLowerCase();

    if (query) {
      result = result.filter((item) =>
        (item.headline || "").toLowerCase().includes(query) ||
        (Array.isArray(item.tags) && item.tags.some((tag) => String(tag).toLowerCase().includes(query)))
      );
    }

    if (dateFilter !== "all") {
      const now = new Date();
      const startDate = dateFilter === "today" ? subDays(now, 1) : dateFilter === "week" ? subDays(now, 7) : subDays(now, 30);
      result = result.filter((item) => {
        try {
          const itemDate = parseISO(item.published_at);
          return !Number.isNaN(itemDate.getTime()) && isWithinInterval(itemDate, { start: startDate, end: now });
        } catch (dateError) {
          return false;
        }
      });
    }

    return result;
  }, [news, searchQuery, dateFilter]);

  const openArticle = useCallback((id) => navigate(`/news/${id}`), [navigate]);

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-surface-950 px-5 pb-10 pt-24 md:pt-20 sm:px-8">
          <div role="status" aria-label="Loading news" className="mx-auto flex min-h-72 max-w-7xl items-center justify-center rounded-xl border border-line bg-surface-900">
            <CircularProgress size={32} sx={{ color: "#d81f2a" }} />
            <span className="sr-only">Loading news articles</span>
          </div>
        </main>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-surface-950 px-5 pb-10 pt-24 md:pt-20 sm:px-8">
          <div role="alert" className="mx-auto max-w-3xl rounded-xl border border-line bg-surface-900 p-8 text-center">
            <NewspaperIcon aria-hidden="true" className="mx-auto h-9 w-9 text-gray-500" />
            <h1 className="mt-4 text-xl font-semibold text-white">News is temporarily unavailable</h1>
            <p className="mt-2 text-sm text-gray-400">{error}</p>
            <button type="button" onClick={() => window.location.reload()} className="btn-primary mt-5">Try again</button>
          </div>
        </main>
      </>
    );
  }

  const featuredArticle = filteredNews[0];
  const remainingArticles = filteredNews.slice(1);

  return (
    <>
      <Navbar />
      <main className="min-h-screen overflow-x-hidden bg-surface-950 pb-12 pt-16 text-white md:pt-0">
        <section className="border-b border-line bg-surface-900">
          <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 5, md: 6 }, px: { xs: 2.5, sm: 4, lg: 5 } }}>
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                <p className="eyebrow-accent inline-flex items-center gap-2">
                  <NewspaperIcon aria-hidden="true" className="h-4 w-4" />
                  THE LATEST
                </p>
                <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">News & stories</h1>
                <p className="mt-2 text-sm leading-6 text-gray-400 sm:text-base">Stay informed with the latest stories and developments.</p>
              </div>

              <div className="grid w-full gap-3 sm:grid-cols-[minmax(0,1fr)_200px] lg:max-w-xl">
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search headlines or tags…"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  inputProps={{ "aria-label": "Search news headlines or tags" }}
                  InputProps={{
                    startAdornment: <MagnifyingGlassIcon aria-hidden="true" className="mr-2 h-5 w-5 shrink-0 text-gray-500" />,
                    endAdornment: searchQuery ? (
                      <button type="button" aria-label="Clear search" onClick={() => setSearchQuery("")} className="rounded-md p-1 text-gray-500 transition-colors hover:bg-surface-700 hover:text-white">
                        <XMarkIcon aria-hidden="true" className="h-4 w-4" />
                      </button>
                    ) : null,
                  }}
                  sx={fieldStyles}
                />
                <TextField
                  select
                  fullWidth
                  value={dateFilter}
                  onChange={(event) => setDateFilter(event.target.value)}
                  inputProps={{ "aria-label": "Filter news by date" }}
                  sx={fieldStyles}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          bgcolor: "#161618",
                          color: "#f9fafb",
                          border: "1px solid #26262a",
                          "& .MuiMenuItem-root.Mui-selected": { bgcolor: "rgba(216,31,42,.16)" },
                          "& .MuiMenuItem-root:hover": { bgcolor: "#26262a" },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="all">All time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">Last 7 days</MenuItem>
                  <MenuItem value="month">Last 30 days</MenuItem>
                </TextField>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-gray-500" aria-live="polite">
              <CalendarDaysIcon aria-hidden="true" className="h-4 w-4" />
              {filteredNews.length} {filteredNews.length === 1 ? "story" : "stories"}
              {searchQuery || dateFilter !== "all" ? " found" : ""}
            </div>
          </Container>
        </section>

        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 5 }, px: { xs: 2.5, sm: 4, lg: 5 } }}>
          {filteredNews.length === 0 ? (
            <section className="rounded-xl border border-line bg-surface-900 px-5 py-14 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-line bg-surface-850">
                <MagnifyingGlassIcon aria-hidden="true" className="h-5 w-5 text-gray-500" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-white">No stories found</h2>
              <p className="mt-2 text-sm text-gray-400">Try another search term or date range.</p>
              {(searchQuery || dateFilter !== "all") && (
                <button type="button" onClick={() => { setSearchQuery(""); setDateFilter("all"); }} className="btn-secondary mt-5">
                  Clear filters
                </button>
              )}
            </section>
          ) : (
            <>
              {featuredArticle && (
                <section aria-label="Featured news" className="mb-8">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                    <p className="eyebrow-accent">FEATURED STORY</p>
                  </div>
                  <ArticleCard article={featuredArticle} featured onOpen={openArticle} formatDate={formatDate} getFirstImage={getFirstImage} getTextContent={getTextContent} />
                </section>
              )}

              {remainingArticles.length > 0 && (
                <section aria-label="More news">
                  <div className="mb-4 flex items-end justify-between gap-4">
                    <div>
                      <p className="eyebrow">LATEST UPDATES</p>
                      <h2 className="mt-1 text-xl font-semibold tracking-tight text-white">More stories</h2>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {remainingArticles.map((article) => (
                      <ArticleCard key={article.id} article={article} onOpen={openArticle} formatDate={formatDate} getFirstImage={getFirstImage} getTextContent={getTextContent} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </Container>
      </main>
    </>
  );
};

function ArticleCard({ article, featured = false, onOpen, formatDate, getFirstImage, getTextContent }) {
  const image = getFirstImage(article.content);
  const tags = Array.isArray(article.tags) ? article.tags : [];

  return (
    <article
      role="link"
      tabIndex={0}
      aria-label={`Read article: ${article.headline}`}
      onClick={() => onOpen(article.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(article.id);
        }
      }}
      className={`group min-w-0 cursor-pointer overflow-hidden rounded-xl border border-line bg-surface-850 transition-all duration-200 hover:-translate-y-0.5 hover:border-line-strong hover:bg-surface-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${featured ? "grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]" : "flex flex-col"}`}
    >
      {image && (
        <div className={`${featured ? "h-56 sm:h-72 lg:h-full lg:min-h-[320px]" : "h-48"} overflow-hidden bg-surface-900`}>
          <img
            src={image}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.025]"
          />
        </div>
      )}
      <div className={`flex min-w-0 flex-1 flex-col ${featured ? "justify-center p-5 sm:p-7 lg:p-9" : "p-5"}`}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {tags.slice(0, featured ? 3 : 2).map((tag, index) => (
            <span key={`${tag}-${index}`} className="rounded-md border border-accent/20 bg-accent/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-accent-soft">
              {tag}
            </span>
          ))}
          <time className="ml-auto text-xs text-gray-500" dateTime={article.published_at || undefined}>
            {formatDate(article.published_at)}
          </time>
        </div>
        <h2 className={`${featured ? "text-2xl sm:text-3xl" : "text-lg"} font-semibold leading-snug tracking-tight text-white transition-colors group-hover:text-accent-soft`}>
          {article.headline}
        </h2>
        <p className={`${featured ? "mt-4 text-base" : "mt-3 text-sm"} leading-6 text-gray-400`}>
          {getTextContent(article.content) || "Open this story to read the full report."}
        </p>
        <div className="mt-auto flex items-center justify-between gap-3 pt-5">
          <span className="text-xs font-medium text-gray-500">DESAC TV News</span>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-300 transition-colors group-hover:text-accent-soft">
            Read story <ArrowRightIcon aria-hidden="true" className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </article>
  );
}

const fieldStyles = {
  "& .MuiOutlinedInput-root": {
    minHeight: 46,
    color: "#f9fafb",
    bgcolor: "#161618",
    borderRadius: "10px",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "#3a3a40" },
    "&:hover fieldset": { borderColor: "#52525b" },
    "&.Mui-focused fieldset": { borderColor: "#d81f2a", borderWidth: "1px" },
  },
  "& .MuiInputBase-input": { py: "11px", px: "12px" },
  "& .MuiSelect-icon": { color: "#9ca3af" },
};

export default News;
